import { useCallback, useMemo, useRef } from "react"
import { useRouter } from "next/router"

import { logger } from "@/lib/logger"
import {
  buildWorkspaceAwareNavigation,
  parseWorkspaceRoute,
} from "@/utils/workspaceRoutes"

import {
  createQueryHref,
  getCanonicalDestinationPath,
  getCurrentCanonicalPath,
  splitPathQueryHash,
} from "./queryParamsNavigation"

const log = logger("QueryParams")

const PRESERVED_NAV_QUERY_KEYS = new Set(["agent_id", "fromAgentId"])

/**
 * Options for query parameter updates that affect URL navigation.
 */
interface QueryUpdateOptions {
  history?: "push" | "replace"
  shallow?: boolean
}

/**
 * Interface for query parameter management
 */
interface QueryParams {
  /** Get a single query parameter value */
  get: (key: string) => string | null
  /** Get all values for a query parameter (useful for array parameters) */
  getAll: (key: string) => string[]
  /** Check if a query parameter exists */
  has: (key: string) => boolean
  /** Get all query parameters as key-value pairs */
  entries: () => [string, string][]
  /** Set a single query parameter value */
  set: (key: string, value: string | null) => void
  /** Set multiple query parameters at once */
  setMultiple: (
    params: Record<string, string | null>,
    options?: QueryUpdateOptions,
  ) => void
  /** Remove a query parameter */
  remove: (key: string) => void
  /** Clear all query parameters */
  clear: () => void
  /** Get the current URL with query parameters */
  getHref: (baseHref?: string, orgSlug?: string, workspaceId?: string) => string
  /** Navigate to a new page while preserving query parameters (including agent_id) */
  push: (
    path: string,
    orgSlug?: string,
    options?: { query?: Record<string, string | string[] | null> },
  ) => void
}

export interface QueryHrefParams {
  getHref: (path: string, orgSlug?: string, workspaceId?: string) => string
}

/**
 * Helper function to clean URL parameters
 */
/**
 * Low-level hook for imperative URL query parameter reads and navigation.
 * Uses `router.push` — each write creates a browser history entry.
 *
 * Use this for: reading params, navigation (`push`, `getHref`), and
 * one-shot writes like `agent_id` that aren't part of a filter schema.
 *
 * **Do NOT use for filter state.** Use `useFilterParams` instead — it
 * provides debounced `router.replace` (no history pollution), localStorage
 * fallback, and schema-driven serialization. The two hooks preserve each
 * other's keys, but writing the same key from both on one page will race.
 */
export const useQueryParams = (): QueryParams => {
  const router = useRouter()

  // Extract dynamic route parameter names from the pathname
  const routeParams = useMemo(() => {
    const matches = router.pathname.match(/\[([^\]]+)\]/g)
    if (!matches) {
      return new Set<string>()
    }
    return new Set(matches.map((match) => match.slice(1, -1))) // Remove [ and ]
  }, [router.pathname])

  // Convert router.query to URLSearchParams for consistent internal API
  // Filter out dynamic route parameters (like [slug], [run_id], etc.)
  const searchParams = useMemo(() => {
    const params = new URLSearchParams()
    Object.entries(router.query).forEach(([key, value]) => {
      // Skip if this is a dynamic route parameter
      if (routeParams.has(key)) {
        return
      }

      if (Array.isArray(value)) {
        // For array values, add all entries
        value.forEach((v) => params.append(key, v))
      } else if (value !== undefined) {
        params.set(key, value)
      }
    })
    return params
  }, [router.query, routeParams])

  const updateUrlParams = useCallback(
    (params: URLSearchParams, options?: QueryUpdateOptions) => {
      try {
        const currentCanonicalPath = getCurrentCanonicalPath(router.asPath)
        const navigationOptions =
          (options?.shallow ?? Boolean(currentCanonicalPath))
            ? { shallow: true }
            : undefined

        // Convert URLSearchParams back to query object
        const queryObj: Record<string, string | string[]> = {}

        // First, preserve all dynamic route parameters (slug, dashboardId, etc.)
        routeParams.forEach((paramName) => {
          const value = router.query[paramName]
          if (value !== undefined) {
            queryObj[paramName] = value
          }
        })

        // Group params by key to handle multiple values
        const paramsByKey = new Map<string, string[]>()
        for (const [key, value] of params.entries()) {
          if (value && value.trim() !== "") {
            if (!paramsByKey.has(key)) {
              paramsByKey.set(key, [])
            }
            paramsByKey.get(key)!.push(value)
          }
        }

        // Convert to query object format (merge with route params)
        paramsByKey.forEach((values, key) => {
          queryObj[key] = values.length === 1 ? values[0] : values
        })

        if (currentCanonicalPath) {
          // A shallow update stays on the current page, so the router resolves
          // the target client-side and never sees the middleware rewrite that
          // maps a canonical workspace URL onto its internal `[slug]` route.
          // Navigating to the canonical URL alone therefore drops the dynamic
          // route params from `router.query`; pairing the internal href with
          // the canonical `as` path keeps them.
          if (navigationOptions) {
            const navigation = buildWorkspaceAwareNavigation(
              router.asPath,
              router.pathname,
              queryObj,
            )

            if (options?.history === "replace") {
              void router.replace(
                navigation.href,
                navigation.as,
                navigationOptions,
              )
            } else {
              void router.push(
                navigation.href,
                navigation.as,
                navigationOptions,
              )
            }
            return
          }

          const { hash } = splitPathQueryHash(router.asPath)
          const nextLocation = createQueryHref(
            currentCanonicalPath,
            params,
            hash,
          )

          if (options?.history === "replace") {
            void router.replace(nextLocation)
          } else {
            void router.push(nextLocation)
          }
          return
        }

        const nextLocation = {
          pathname: router.pathname,
          query: queryObj,
        }

        if (options?.history === "replace") {
          if (navigationOptions) {
            void router.replace(nextLocation, undefined, navigationOptions)
          } else {
            void router.replace(nextLocation)
          }
        } else if (navigationOptions) {
          void router.push(nextLocation, undefined, navigationOptions)
        } else {
          void router.push(nextLocation)
        }
      } catch (error) {
        log.warn("Error updating URL parameters", {
          error: error instanceof Error ? error.message : String(error),
        })
      }
    },
    [router, routeParams],
  )

  // Use ref to avoid including updateUrlParams in dependency arrays
  // This prevents cascade re-renders when any query param changes
  const updateUrlParamsRef = useRef(updateUrlParams)
  updateUrlParamsRef.current = updateUrlParams

  const routerRef = useRef(router)
  routerRef.current = router

  const routeParamsRef = useRef(routeParams)
  routeParamsRef.current = routeParams

  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  return useMemo(() => {
    const get = (key: string): string | null => {
      const value = routerRef.current.query[key]
      if (Array.isArray(value)) {
        return value[0] || null
      }
      return value || null
    }

    const getAll = (key: string): string[] => {
      const value = routerRef.current.query[key]
      if (Array.isArray(value)) {
        return value
      }
      return value ? [value] : []
    }

    const has = (key: string): boolean => {
      return key in routerRef.current.query
    }

    const entries = (): [string, string][] => {
      const result: [string, string][] = []
      Object.entries(routerRef.current.query).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => result.push([key, v]))
        } else if (value !== undefined) {
          result.push([key, value])
        }
      })
      return result
    }

    const set = (key: string, value: string | null) => {
      try {
        const params = new URLSearchParams(searchParamsRef.current.toString())
        if (!value || value.trim() === "") {
          params.delete(key)
        } else {
          params.set(key, value)
        }
        updateUrlParamsRef.current(params)
      } catch (error) {
        log.warn(`Error setting parameter ${key}`, {
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    const setMultiple = (
      newParams: Record<string, string | null>,
      options?: QueryUpdateOptions,
    ) => {
      try {
        const params = new URLSearchParams(searchParamsRef.current.toString())
        Object.entries(newParams).forEach(([key, value]) => {
          if (!value || value.trim() === "") {
            params.delete(key)
          } else {
            params.set(key, value)
          }
        })
        updateUrlParamsRef.current(params, options)
      } catch (error) {
        log.warn("Error setting multiple parameters", {
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    const remove = (key: string) => {
      try {
        const params = new URLSearchParams(searchParamsRef.current.toString())
        params.delete(key)
        updateUrlParamsRef.current(params)
      } catch (error) {
        log.warn(`Error removing parameter ${key}`, {
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    const clear = () => {
      try {
        // Preserve dynamic route params when clearing query params
        const queryObj: Record<string, string | string[]> = {}
        routeParamsRef.current.forEach((paramName) => {
          const value = routerRef.current.query[paramName]
          if (value !== undefined) {
            queryObj[paramName] = value
          }
        })

        const currentCanonicalPath = getCurrentCanonicalPath(
          routerRef.current.asPath,
        )
        if (currentCanonicalPath) {
          const navigation = buildWorkspaceAwareNavigation(
            routerRef.current.asPath,
            routerRef.current.pathname,
            queryObj,
          )
          void routerRef.current.push(navigation.href, navigation.as, {
            shallow: true,
          })
          return
        }

        void routerRef.current.push({
          pathname: routerRef.current.pathname,
          query: queryObj,
        })
      } catch (error) {
        log.warn("Error clearing parameters", {
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    const getHref = (
      baseHref?: string,
      orgSlug?: string,
      workspaceId?: string,
    ) => {
      try {
        // Only preserve specific query params across navigation
        const paramsToPreserve = PRESERVED_NAV_QUERY_KEYS
        const filteredParams = new URLSearchParams()

        for (const [key, value] of searchParamsRef.current.entries()) {
          if (paramsToPreserve.has(key) && value && value.trim() !== "") {
            filteredParams.set(key, value)
          }
        }

        const currentCanonicalRoute = parseWorkspaceRoute(
          routerRef.current.asPath ?? "",
        )

        const effectiveCanonicalRoute =
          workspaceId && orgSlug
            ? {
                organizationSlug: orgSlug,
                workspaceId,
                relativePath: "",
              }
            : currentCanonicalRoute

        if (!baseHref || baseHref === "#") {
          const currentCanonicalPath = getCurrentCanonicalPath(
            routerRef.current.asPath,
          )
          const currentPath = currentCanonicalPath ?? routerRef.current.pathname
          return createQueryHref(currentPath, filteredParams)
        }

        // Split baseHref into pathname and any inline query params
        const {
          path: basePath,
          query: baseQuery,
          hash,
        } = splitPathQueryHash(baseHref)
        if (baseQuery) {
          const baseParams = new URLSearchParams(baseQuery)
          for (const [key, value] of baseParams.entries()) {
            filteredParams.set(key, value)
          }
        }

        const path = getCanonicalDestinationPath(
          basePath,
          orgSlug,
          effectiveCanonicalRoute,
        )
        return createQueryHref(path, filteredParams, hash)
      } catch (error) {
        log.warn("Error generating href", {
          error: error instanceof Error ? error.message : String(error),
        })
        return routerRef.current.pathname // Fallback to current pathname on error
      }
    }

    const push = (
      path: string,
      orgSlug?: string,
      options?: { query?: Record<string, string | string[] | null> },
    ) => {
      try {
        const {
          path: fullPath,
          query,
          hash,
        } = splitPathQueryHash(getHref(path, orgSlug))
        const params = new URLSearchParams(query)

        // Apply any additional/override query params
        if (options?.query) {
          Object.entries(options.query).forEach(([key, value]) => {
            if (value === null) {
              params.delete(key)
            } else if (Array.isArray(value)) {
              params.delete(key)
              value.forEach((v) => {
                if (v !== null) {
                  params.append(key, v)
                }
              })
            } else {
              params.set(key, value)
            }
          })
        }

        const url = createQueryHref(fullPath, params, hash)
        void routerRef.current.push(url)
      } catch (error) {
        log.warn("Error navigating with query params", {
          error: error instanceof Error ? error.message : String(error),
        })
        // Fallback to simple navigation
        const cleanPath = path.replace("/[slug]", "")
        const fullPath = orgSlug ? `/${orgSlug}${cleanPath}` : cleanPath
        void routerRef.current.push(fullPath)
      }
    }

    return {
      get,
      getAll,
      has,
      entries,
      set,
      setMultiple,
      remove,
      clear,
      getHref,
      push,
    }
  }, [])
}
